<template>
  <div
    v-if="data && reArrangeElements && reArrangeElements.length > 0"
    class="section-layout-wrapper"
  >
    <slot
      v-for="(section, sectionIndex) in data.sectionsExpanded"
      :key="sectionIndex"
      :name="handleTemplateName(section.settings.template, sectionIndex)"
      :data="section"
      :component-index="sectionIndex"
    >
      <section
        v-if="section"
        class="section"
        :class="[
          handleStringFormat(section.settings.class),
          handleStringFormat(section.settings.template)
        ]"
        :data-section-index="sectionIndex"
        :data-template-name="handleTemplateName(section.settings.template, sectionIndex)"
      >
        <span
          v-if="section.settings.anchorPoint && section.settings.anchorPoint !== ''"
          :id="section.settings.anchorPoint"
          class="anchor"
        ></span>
        <span class="bg-el"></span>
        <div class="container">
          <slot :name="'header-' + handleTemplateName(section.settings.template, sectionIndex)">
            <div
              class="section-header-wrapper"
              v-if="
                (section.title && section.title !== '') ||
                (section.subtitle && section.subtitle !== '')
              "
            >
              <h2
                v-if="section.title && section.title !== ''"
                class="section-title"
                v-html="checkShortcode(section.title)"
              ></h2>
              <span
                v-if="section.subtitle && section.subtitle !== ''"
                class="section-subtitle"
                v-html="checkShortcode(section.subtitle)"
              >
              </span>
            </div>
          </slot>

          <!-- Collection content i.e. TemplateContent -->
          <div
            v-if="section.contentCollection && section.contentCollection.length > 0"
            class="column-wrapper"
          >
            <div
              class="collection"
              :class="['column ' + getUnicByKey(section.contentCollection, 'contentName', true)]"
            >
              <div class="items-wrapper">
                <ul>
                  <li
                    v-for="(element, elementIndex) in section.contentCollection"
                    :key="elementIndex"
                    :class="getUnicByKey(section.contentCollection, 'contentName', true)"
                  >
                    <SingleTemplateContent
                      :data="element.templateContent"
                      :content-type="element.contentName.toString().toLowerCase()"
                    />
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Std -->
          <div
            v-else-if="
              section.elements &&
              section.elements.length > 0 &&
              section.contentCollection &&
              section.contentCollection.length == 0
            "
            class="column-wrapper"
            :class="[
              'column-count-' + section.elements.length,
              buildContainsClasslist(section.elements)
            ]"
          >
            <div
              v-for="(element, elementIndex) in section.elements"
              :key="elementIndex"
              :class="[
                'column ' + element.entityType.toLowerCase(),
                addComponentNameAsClass(element)
              ]"
            >
              <PlayList
                v-if="
                  element.playList &&
                  element.playList.relatedContents &&
                  element.playList.relatedContents.length > 0 &&
                  element.entityType === 'Playlist' &&
                  getUnicByKey(element.playList.relatedContents, 'entityType').includes(
                    'VideoContent'
                  )
                "
                :playlist-expanded="element.playList"
              />

              <ContentPlayList
                v-if="
                  element.playList &&
                  element.playList.relatedContents &&
                  element.playList.relatedContents.length > 0 &&
                  element.entityType === 'Playlist' &&
                  getUnicByKey(element.playList.relatedContents, 'entityType').includes(
                    'TemplateContent'
                  )
                "
                :data="element.playList"
                :content-type="getUnicByKey(element.playList.relatedContents, 'contentName', true)"
              />
              <PictureColumn
                v-if="
                  element.entityType === 'TemplateContent' &&
                  element.contentType === 'Component' &&
                  element.contentName.toString().toLowerCase() === 'picture'
                "
                :data="element"
              />

              <!-- Component -->
              <ContentColumn
                v-else-if="
                  element.entityType === 'TemplateContent' && element.contentType === 'Component'
                "
                :data="element"
              />

              <!-- Price plan -->
              <SubscriptionOverviewItem
                v-if="element.entityType == 'PricePlan'"
                :data="element.pricePlan"
              />

              <OverviewCollection v-if="element.entityType === 'Overview'" :sectionData="element" />
            </div>
          </div>

          <slot
            :name="'footer-' + handleTemplateName(section.settings.template, sectionIndex)"
          ></slot>
        </div>
      </section>
    </slot>
  </div>
  <!-- Fallback -->
  <div class="section-layout-wrapper" v-else></div>
</template>

<script>
//Vue
import { defineComponent, defineAsyncComponent, ref, computed } from 'vue'

//Composables
import { useAuthenticationData } from '@/composables/useAuthenticationData'

// @import component
const PlayList = defineAsyncComponent(
  () => import('@/components/PageLayoutBuilder/Partials/Video/PlayList/index.vue')
)
const ContentColumn = defineAsyncComponent(
  () => import('@/components/PageLayoutBuilder/Partials/Content/ContentColumn/index.vue')
)
const PictureColumn = defineAsyncComponent(
  () => import('@/components/PageLayoutBuilder/Partials/Content/PictureColumn/index.vue')
)
const ContentPlayList = defineAsyncComponent(
  () => import('@/components/PageLayoutBuilder/Partials/Content/ContentPlayList/index.vue')
)
const SubscriptionOverviewItem = defineAsyncComponent(
  () => import('@/components/SubscriptionOverviewItem/index.vue')
)

const SingleTemplateContent = defineAsyncComponent(
  () => import('@/components/PageLayoutBuilder/Partials/Content/SingleTemplateContent/index.vue')
)

const OverviewCollection = defineAsyncComponent(
  () => import('@/components/PageLayoutBuilder/Partials/OverviewCollection/index.vue')
)

export default defineComponent({
  components: {
    PlayList,
    ContentColumn,
    ContentPlayList,
    SubscriptionOverviewItem,
    SingleTemplateContent,
    OverviewCollection,
    PictureColumn
  },
  // props: ['data', 'addSectionClass'],
  props: ['data'],
  setup(props) {
    const isAuthenticated = computed(() => {
      return useAuthenticationData().data.value.isAuthenticated
    })

    const reArrangeElements = computed(() => {
      return props.data.sectionsExpanded?.filter(function (section) {
        if (section.elements && section.elements.length > 0) {
          let elementConversionArray = []

          section.elements.forEach((element) => {
            if (element.entityType === 'TemplateContent' && element.contentType === 'Page') {
              elementConversionArray.push(element)
            }
          })

          return (section.contentCollection = elementConversionArray)
        }
      })
    })

    const addedClassesThroughTemplate = ref([])
    const contentTypeArray = ref([])

    // function groupByList(list, keyGetter) {
    //     const map = new Map()
    //     list.forEach((item) => {
    //         const key = keyGetter(item)
    //         const collection = map.get(key)
    //         if (!collection) {
    //             map.set(key, [item])
    //         } else {
    //             collection.push(item)
    //         }
    //     })
    //     return map
    // }

    // function groupBy(array, key) {
    //     // Return the end result
    //     return array.reduce((result, currentValue) => {
    //         // If an array already present for key, push it to the array. Else create an array and push the object
    //         ; (result[currentValue[key]] = result[currentValue[key]] || []).push(currentValue)
    //         // Return the current iteration `result` value, this will be taken as next iteration `result` value and accumulate
    //         return result
    //     }, {}) // empty object is the initial value for result object
    // }

    // function parseHtml(passedHtml) {
    //     return passedHtml.replaceAll('rel="noopener noreferrer" target="_blank"', '')
    // }

    function getUnicByKey(items, key, transformToString) {
      const res = new Set()
      if (items && items.length > 0) {
        items.forEach((item) => res.add(item[key]))
      }
      if (transformToString) return Array.from(res).toString().toLowerCase() || ''
      return Array.from(res) || []
    }

    function addComponentNameAsClass(elem) {
      if (elem) {
        if (elem.entityType === 'Playlist') {
          if (elem.playList) {
            return transformContentType(
              getUnicByKey(elem.playList.relatedContents, 'contentName', true)
            )
          }
        } else if (elem.entityType === 'TemplateContent' && elem.contentType !== 'Component') {
          return elem.contentName.toString().toLowerCase().replaceAll(' ', '').replaceAll('+', '-')
        } else if (elem.entityType === 'TemplateContent' && elem.contentType === 'Component') {
          return elem.contentName.toString().toLowerCase().replaceAll(' ', '').replaceAll('+', '-')
        } else {
          return ''
        }
      } else {
        return ''
      }
    }

    function transformContentType(valType) {
      let valString = valType

      let replaceChars = valString.replaceAll(/[^A-Z0-9]/gi, '')
      let returnString = replaceChars.toLowerCase()

      return returnString
    }

    function handleStringFormat(stringValue) {
      if (stringValue && stringValue !== '') {
        return stringValue.replaceAll(',', ' ')
      }

      return ''
    }

    function handleTemplateName(sectionTemplate, sectionIndex) {
      if (sectionTemplate && sectionTemplate !== '') {
        return 'section-' + handleStringFormat(sectionTemplate)
      } else if (sectionIndex && sectionIndex !== '') {
        return 'section-' + sectionIndex
      }
    }

    function buildContainsClasslist(elements) {
      let elementTypesList = []

      const unique = (array) => {
        return array.filter(function (el, index, arr) {
          return index == arr.indexOf(el)
        })
      }

      if (elements && elements.length > 0) {
        elements.forEach((element) => {
          if (element && element.entityType && element.entityType !== '') {
            elementTypesList.push(element.entityType.toString().toLowerCase())
          }
        })

        const uniqueVals = unique(elementTypesList)

        return 'contains-' + uniqueVals.join('-')
      }
    }

    function checkShortcode(stringValue) {
      if (stringValue && stringValue !== '') {
        stringValue = stringValue
          .replaceAll('[bold]', '<span class="bold">')
          .replaceAll('[/bold]', '</span>')
        // stringValue = stringValue
        //   .replaceAll('[italic]', '<span class="italic">')
        //   .replaceAll('[/italic]', '</span>')
        // stringValue = stringValue
        //   .replaceAll('[underline]', '<span class="underline">')
        //   .replaceAll('[/underline]', '</span>')
        // stringValue = stringValue
        //   .replaceAll('[strikethrough]', '<span class="strikethrough">')
        //   .replaceAll('[/strikethrough]', '</span>')
        // stringValue = stringValue.replaceAll('[link]', '<a href="').replaceAll('[/link]', '</a>')
        // stringValue = stringValue.replaceAll('[link]', '<a href="').replaceAll('[/link]', '</a>')
      }

      return stringValue
    }

    return {
      isAuthenticated,
      reArrangeElements,
      addedClassesThroughTemplate,
      contentTypeArray,
      //functions
      // groupByList,
      // groupBy,
      // parseHtml,
      getUnicByKey,
      addComponentNameAsClass,
      transformContentType,
      handleStringFormat,
      handleTemplateName,
      buildContainsClasslist,
      checkShortcode
    }
  }
})
</script>

<style lang="scss" scoped>
// @import styles
@import './index.scss';
</style>

<style lang="scss">
// @import styles
@import './index-unscoped.scss';
</style>
